import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './AdminLogin.css';

// Opened from the "choose a new password" email. Supabase signs the user in from the link (a recovery session);
// staff with an authenticator must also enter their code before the password can be changed.
const MIN_LENGTH = 8;

const AdminResetPassword = () => {
  const { loading, isAuthenticated, mfaRequired, verifyMfa, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submitCode = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const result = await verifyMfa(code);
    setBusy(false);
    if (!result.success) {
      setError(result.message);
      setCode('');
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match');
      return;
    }
    setBusy(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setBusy(false);
      // Auth's validation messages (e.g. "should be different from the old password") are written for users.
      setError(updateError.status === 422 ? updateError.message : 'The password could not be changed. Please request a new link.');
      return;
    }
    await logout();
    navigate('/admin/login', {
      replace: true,
      state: { message: 'Your password has been changed. Sign in with your new password.' }
    });
  };

  let body;
  if (loading) {
    body = <p role="status">Checking your link...</p>;
  } else if (mfaRequired) {
    body = (
      <form onSubmit={submitCode} className="admin-login-form">
        <p>Your account uses two-step sign-in. Enter the code from your authenticator app to continue.</p>
        <div className="form-group">
          <label htmlFor="reset-code">Authentication code</label>
          <input
            id="reset-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            disabled={busy}
          />
        </div>
        <button type="submit" className="btn-login" disabled={busy || code.length !== 6}>Continue</button>
      </form>
    );
  } else if (!isAuthenticated) {
    body = (
      <div className="admin-login-form">
        <p role="alert">This link is invalid or has expired. Links can be used once and expire after one hour.</p>
        <Link to="/admin/login" className="btn-login">Back to sign in</Link>
      </div>
    );
  } else {
    body = (
      <form onSubmit={submitPassword} className="admin-login-form" noValidate>
        <div className="form-group">
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={MIN_LENGTH}
            disabled={busy}
            aria-describedby="new-password-hint"
          />
          <small id="new-password-hint">At least {MIN_LENGTH} characters.</small>
        </div>
        <div className="form-group">
          <label htmlFor="confirm-password">Confirm new password</label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy}
          />
        </div>
        <button type="submit" className="btn-login" disabled={busy}>{busy ? 'Saving...' : 'Change password'}</button>
      </form>
    );
  }

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <img src="/logo.png" alt="KS Montessori & Secondary School" className="admin-logo" />
            <h1>Choose a new password</h1>
          </div>
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          {body}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPassword;
